import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Train, Bus, Car, ParkingSquare, Plane } from 'lucide-react';

const accessConfig = [
  { id: 'tram', icon: Train, color: 'bg-blue-600' },
  { id: 'bus', icon: Bus, color: 'bg-emerald-600' },
  { id: 'car', icon: Car, color: 'bg-slate-700' },
  { id: 'parking', icon: ParkingSquare, color: 'bg-amber-600' },
  { id: 'train', icon: Train, color: 'bg-rose-600' },
  { id: 'airport', icon: Plane, color: 'bg-indigo-600' },
];

export const Access = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="access" data-testid="access-section" ref={ref} className="py-16 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-amber-600 font-inter text-sm font-semibold tracking-wider uppercase">
            {t('access.tag')}
          </span>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 mb-6">
            {t('access.title1')} <span className="italic">{t('access.title1Italic')}</span> ?
          </h2>
          <div className="section-divider mb-6" />
          <p className="font-inter text-slate-600 text-lg max-w-2xl mx-auto">
            {t('access.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {accessConfig.map((option, index) => (
            <motion.article
              key={option.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.08 * index }}
              data-testid={`access-card-${option.id}`}
              className="bg-white rounded-2xl border border-slate-200 p-6 card-hover group"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl ${option.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  <option.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-playfair text-xl font-bold text-slate-900 leading-tight">
                    {t(`access.options.${option.id}.title`)}
                  </h3>
                  <p className="font-inter text-sm font-semibold text-amber-600 mt-1 mb-2">
                    {t(`access.options.${option.id}.lines`)}
                  </p>
                  <p className="font-inter text-slate-600 text-sm leading-relaxed">
                    {t(`access.options.${option.id}.description`)}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          data-testid="access-address-band"
          className="mt-12 bg-slate-900 rounded-3xl p-8 lg:p-10 flex flex-col lg:flex-row items-center justify-between gap-6"
        >
          <div className="text-center lg:text-left">
            <p className="font-caveat text-xl text-amber-400 mb-2">{t('access.addressHandwritten')}</p>
            <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-white">
              {t('access.address')}
            </h3>
          </div>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=10+Avenue+Georges+Clemenceau+06000+Nice"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="access-directions-btn"
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-full px-6 py-3 transition-colors inline-flex items-center gap-2 btn-shine"
          >
            <Car className="w-5 h-5" />
            {t('access.directions')}
          </a>
        </motion.div>
      </div>
    </section>
  );
};
